import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layout } from '../components/layout/Layout';
import { ComicHeading } from '../components/ComicPanel';
import { ContentCard } from '../components/ContentCard';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const sectionTitles = {
  skits: 'Skits',
  'tob-skits': 'TOB Skits',
  'sporc-minute': 'Sporc Minute',
  'sporc-sparks': 'Sporc Sparks',
  'piggee-pen-case-files': 'Piggee Pen Case Files',
  lore: 'Lore',
  bonus: 'Bonus Content',
  updates: 'Updates',
  interactive: 'Interactive',
};

const sectionDescriptions = {
  skits: 'Main episodes and stories from the Pork Cops universe.',
  'tob-skits': 'Tower of Beef shorts, villain scenes, and Beef Wellington skits.',
  'sporc-minute': 'Short Pork Cops explainers made for members and social clips.',
  'sporc-sparks': 'Quick comedy bits, jokes, and short Pork Cops moments.',
  'piggee-pen-case-files': 'Piggee Pen investigations, odd reports, and case file skits.',
  lore: 'Characters, places, factions, Sporc Energy, and Planet Livestock worldbuilding.',
  bonus: 'Behind the scenes, art process, and exclusive extras.',
  updates: 'News, announcements, and development updates.',
  interactive: 'Choose your own adventure. Your decisions matter.',
};

const Library = () => {
  const { section } = useParams();
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchContent();
  }, [section, token]);

  const fetchContent = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/content/library/${section}`, { headers });
      setItems(response.data);
    } catch (err) {
      console.error('Failed to fetch content:', err);
      if (err.response?.status === 403) {
        setError('This section requires a verified account.');
      } else {
        setError('Failed to load content. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const title = sectionTitles[section] || section;
  const description = sectionDescriptions[section] || '';

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Back Link */}
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-slate-400 hover:text-yellow-500 mb-8 transition-colors"
          data-testid="library-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        {/* Header */}
        <div className="mb-12">
          <ComicHeading as="h1" className="text-3xl sm:text-4xl mb-4" data-testid="library-title">
            {title}
          </ComicHeading>
          <p className="text-slate-400 text-lg">{description}</p>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <p className="text-red-500 mb-4">{error}</p>
            <Link to="/dashboard" className="text-yellow-500 hover:underline">
              Return to Dashboard
            </Link>
          </div>
        )}

        {/* Content Grid */}
        {!loading && !error && (
          <>
            {items.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-slate-400 text-lg">No content available yet.</p>
                <p className="text-slate-500 mt-2">Check back soon!</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="library-grid">
                {items.map((item) => (
                  <ContentCard key={item.id} item={item} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Library;
